import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Storefront, Lock, Plus, Trash, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];
const LANDING_SLUGS = [
  "personal-injury","free-case-review","auto-accident","truck-accident","rideshare-accident","motorcycle-accident",
];

/**
 * Iter-35: Exclusivity Rules manager. Mounted inline on /admin/landing-pages
 * beneath the totals strip so admins can see the routing overrides at the
 * same place they browse the pages themselves.
 */
export default function ExclusivityRules() {
  const [rules, setRules] = useState([]);
  const [dests, setDests] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    landing_page: "personal-injury",
    state: "",
    buyer_destination_id: "",
    reason: "",
  });

  const load = () =>
    api.get("/admin/landing-pages/exclusivity")
      .then((r) => setRules(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load rules"));

  useEffect(() => {
    load();
    // Load all delivery destinations for the "assign to" dropdown.
    api.get("/delivery/destinations")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.destinations || []);
        setDests(list.filter((d) => d.is_active));
      })
      .catch(() => { /* silent */ });
  }, []);

  const submit = async () => {
    if (!form.buyer_destination_id) {
      return toast.error("Pick a destination first");
    }
    setBusy(true);
    try {
      await api.post("/admin/landing-pages/exclusivity", {
        landing_page: form.landing_page,
        state: form.state || null,
        buyer_destination_id: form.buyer_destination_id,
        reason: form.reason.trim() || null,
      });
      toast.success("Rule created");
      setShowAdd(false);
      setForm({ landing_page: "personal-injury", state: "", buyer_destination_id: "", reason: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete exclusivity for ${rule.landing_page}${rule.state ? "/" + rule.state : ""}?`)) return;
    try {
      await api.delete(`/admin/landing-pages/exclusivity/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="brut-card mb-6" data-testid="lp-exclusivity">
      <div className="border-b border-black px-5 py-3 flex items-center gap-2">
        <Lock size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Buyer-exclusive rules
        </div>
        <div className="label-mono ml-2">
          State-bar compliance · route all matching traffic to ONE firm
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="lp-add-exclusivity"
          className="ml-auto border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white inline-flex items-center gap-1.5"
        >
          <Plus size={11} weight="bold" /> Add rule
        </button>
      </div>

      <div className="p-4">
        {!rules.length && (
          <div className="label-mono text-zinc-500 py-4 text-center">
            No exclusivity rules configured — all landing-page traffic follows
            normal routing.
          </div>
        )}
        {rules.length > 0 && (
          <table className="w-full text-xs" data-testid="lp-exclusivity-table">
            <thead>
              <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Landing page</th>
                <th className="text-left py-2 px-3">State</th>
                <th className="text-left py-2 px-3">Reserved for</th>
                <th className="text-left py-2 px-3">Reason</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-zinc-200"
                  data-testid={`lp-rule-${r.id}`}
                >
                  <td className="py-2 px-3 font-mono">/lp/{r.landing_page}</td>
                  <td className="py-2 px-3">
                    {r.state ? (
                      <span className="font-mono font-bold">{r.state}</span>
                    ) : (
                      <span className="bg-amber-100 border border-amber-800 text-amber-900 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                        whole page
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-bold">{r.destination_name || "—"}</div>
                    {r.client_name && (
                      <div className="text-[10px] text-zinc-500">{r.client_name}</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[11px] text-zinc-600">
                    {r.reason || <span className="text-zinc-400 italic">no reason set</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => remove(r)}
                      data-testid={`lp-rule-delete-${r.id}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white hover:border-red-700 inline-flex items-center gap-1"
                    >
                      <Trash size={10} weight="bold" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="lp-add-rule-modal"
        >
          <div className="bg-white border-2 border-black shadow-xl w-full max-w-lg">
            <div className="flex items-center gap-2 border-b-2 border-black px-5 py-3">
              <Storefront size={14} weight="bold" />
              <div className="font-chivo font-black uppercase tracking-tighter">
                New exclusivity rule
              </div>
              <button
                onClick={() => setShowAdd(false)}
                data-testid="lp-add-rule-close"
                className="ml-auto"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label-mono">Landing page</label>
                <select
                  value={form.landing_page}
                  onChange={(e) => setForm({ ...form, landing_page: e.target.value })}
                  data-testid="lp-add-landing"
                  className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                >
                  {LANDING_SLUGS.map((s) => (
                    <option key={s} value={s}>/lp/{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono">State (leave blank for whole page)</label>
                <select
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  data-testid="lp-add-state"
                  className="w-full border border-black px-3 py-2 mt-1 text-sm font-mono"
                >
                  <option value="">— Whole page (any state) —</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono">Reserved buyer destination</label>
                <select
                  value={form.buyer_destination_id}
                  onChange={(e) => setForm({ ...form, buyer_destination_id: e.target.value })}
                  data-testid="lp-add-destination"
                  className="w-full border border-black px-3 py-2 mt-1 text-sm"
                >
                  <option value="">— Select destination —</option>
                  {dests.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.client_id ? "" : "· PLATFORM-WIDE"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono">Reason (audit trail)</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  rows={2}
                  data-testid="lp-add-reason"
                  placeholder="e.g. NY DR 2-103, TN Rule 7.3, exclusive contract with another firm..."
                  className="w-full border border-black px-3 py-2 mt-1 text-xs"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="lp-add-submit"
                  className="bg-black text-white border border-black px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
                >
                  {busy ? "Creating…" : "Create rule"}
                </button>
                <button
                  onClick={() => setShowAdd(false)}
                  className="border border-black px-4 py-2 text-[10px] uppercase tracking-widest"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
